import { FormEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ShopContactSection from "@/components/ShopContactSection";

const REGISTER_DRAFT_STORAGE_KEY = "baobao-register-draft-v1";

type RegisterDraft = {
  name: string;
  email: string;
  password: string;
  password2: string;
  birthMonth: string;
  agreedToTerms: boolean;
  agreedToPrivacy: boolean;
  agreedToPersonalData: boolean;
  ageConfirmed: boolean;
  agreedToCameraUsage: boolean;
};

const BIRTH_MONTH_OPTIONS = [
  { v: "1", label: "1月" },
  { v: "2", label: "2月" },
  { v: "3", label: "3月" },
  { v: "4", label: "4月" },
  { v: "5", label: "5月" },
  { v: "6", label: "6月" },
  { v: "7", label: "7月" },
  { v: "8", label: "8月" },
  { v: "9", label: "9月" },
  { v: "10", label: "10月" },
  { v: "11", label: "11月" },
  { v: "12", label: "12月" },
];

export default function RegisterPage() {
  const { firebaseConfigured, registerWithEmail, loading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const restoredRef = useRef(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [agreedToPersonalData, setAgreedToPersonalData] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [agreedToCameraUsage, setAgreedToCameraUsage] = useState(false);
  const [birthMonth, setBirthMonth] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const displayError = localError || error;

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.sessionStorage.getItem(REGISTER_DRAFT_STORAGE_KEY);
      if (!raw) {
        restoredRef.current = true;
        return;
      }
      const parsed = JSON.parse(raw) as Partial<RegisterDraft>;
      setName(typeof parsed.name === "string" ? parsed.name : "");
      setEmail(typeof parsed.email === "string" ? parsed.email : "");
      setPassword(typeof parsed.password === "string" ? parsed.password : "");
      setPassword2(typeof parsed.password2 === "string" ? parsed.password2 : "");
      setAgreedToTerms(parsed.agreedToTerms === true);
      setAgreedToPrivacy(parsed.agreedToPrivacy === true);
      setAgreedToPersonalData(parsed.agreedToPersonalData === true);
      setAgeConfirmed(parsed.ageConfirmed === true);
      setAgreedToCameraUsage(parsed.agreedToCameraUsage === true);
      setBirthMonth(typeof parsed.birthMonth === "string" ? parsed.birthMonth : "");
    } catch {
      /* ignore broken storage */
    } finally {
      restoredRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !restoredRef.current) return;
    const draft: RegisterDraft = {
      name,
      email,
      password,
      password2,
      birthMonth,
      agreedToTerms,
      agreedToPrivacy,
      agreedToPersonalData,
      ageConfirmed,
      agreedToCameraUsage,
    };
    window.sessionStorage.setItem(REGISTER_DRAFT_STORAGE_KEY, JSON.stringify(draft));
  }, [
    name,
    email,
    password,
    password2,
    agreedToTerms,
    agreedToPrivacy,
    agreedToPersonalData,
    ageConfirmed,
    agreedToCameraUsage,
    birthMonth,
  ]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);
    clearError();

    if (!email.trim()) {
      setLocalError("メールアドレスを入力してください");
      return;
    }
    if (password !== password2) {
      setLocalError("パスワードが一致しません");
      return;
    }
    if (password.length < 8) {
      setLocalError("パスワードは8文字以上にしてください");
      return;
    }
    if (!agreedToTerms) {
      setLocalError("利用規約への同意が必要です");
      return;
    }
    if (!agreedToPrivacy) {
      setLocalError("プライバシーポリシーへの同意が必要です");
      return;
    }
    if (!agreedToPersonalData) {
      setLocalError("個人情報の取得・利用目的への同意が必要です");
      return;
    }
    if (!ageConfirmed) {
      setLocalError("年齢確認への同意が必要です");
      return;
    }
    if (!agreedToCameraUsage) {
      setLocalError("カメラ利用への同意が必要です");
      return;
    }
    if (!birthMonth) {
      setLocalError("誕生月を選択してください");
      return;
    }
    const bm = Number.parseInt(birthMonth, 10);
    if (!Number.isInteger(bm) || bm < 1 || bm > 12) {
      setLocalError("誕生月を正しく選択してください");
      return;
    }

    try {
      await registerWithEmail(name, email, password, {
        termsAccepted: agreedToTerms,
        privacyAccepted: agreedToPrivacy,
        personalDataAccepted: agreedToPersonalData,
        ageConfirmed,
        cameraUsageAccepted: agreedToCameraUsage,
      }, bm);
      if (typeof window !== "undefined") {
        window.sessionStorage.removeItem(REGISTER_DRAFT_STORAGE_KEY);
      }
      navigate("/", { replace: true });
    } catch {
      /* AuthContext にエラー */
    }
  }

  return (
    <div
      className="min-h-screen flex flex-col px-4 py-8"
      style={{
        background: "linear-gradient(160deg, #0d1f1f 0%, #1a2f2f 40%, #0f1a1a 100%)",
        fontFamily: "Noto Sans JP, sans-serif",
      }}
    >
      <div className="max-w-sm mx-auto w-full flex-1 flex flex-col justify-center space-y-6">
        {!firebaseConfigured && (
          <div
            className="rounded-2xl px-4 py-3 text-xs leading-relaxed"
            style={{
              background: "rgba(255,128,128,0.12)",
              border: "1px solid rgba(255,128,128,0.35)",
              color: "#ffb4b4",
            }}
          >
            <p className="font-bold mb-1">Firebase が未設定です</p>
            <p style={{ color: "rgba(255,228,228,0.85)" }}>
              <code className="text-[10px]">.env</code> に <code className="text-[10px]">VITE_FIREBASE_*</code> を設定し、
              開発サーバーを再起動してください。
            </p>
          </div>
        )}

        <div className="text-center">
          <p
            className="text-xs font-bold tracking-widest mb-1"
            style={{ color: "#d4af37", fontFamily: "Montserrat, sans-serif" }}
          >
            Thai Relaxation
          </p>
          <h1
            className="text-2xl font-bold"
            style={{ color: "#f4e5c3", fontFamily: "Montserrat, sans-serif" }}
          >
            会員登録
          </h1>
          <p className="text-xs mt-2" style={{ color: "rgba(244,229,195,0.5)" }}>
            メールでアカウントを作成すると、会員番号が自動で発行されます。
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-xs mb-1" style={{ color: "rgba(244,229,195,0.6)" }}>
              お名前 <span style={{ color: "#ff8080" }}>*</span>
            </label>
            <input
              value={name}
              onChange={(e) => {
                clearError();
                setLocalError(null);
                setName(e.target.value);
              }}
              required
              maxLength={80}
              autoComplete="name"
              className="w-full rounded-xl px-4 py-3 text-base outline-none"
              style={{
                background: "rgba(212,175,55,0.08)",
                border: "1px solid rgba(212,175,55,0.25)",
                color: "#f4e5c3",
              }}
              placeholder="山田 太郎"
            />
          </div>

          <div>
            <label className="block text-xs mb-1" style={{ color: "rgba(244,229,195,0.6)" }}>
              誕生月 <span style={{ color: "#ff8080" }}>*</span>
            </label>
            <select
              value={birthMonth}
              required
              onChange={(e) => {
                clearError();
                setLocalError(null);
                setBirthMonth(e.target.value);
              }}
              className="w-full rounded-xl px-4 py-3 text-base outline-none appearance-none cursor-pointer"
              style={{
                background: "rgba(212,175,55,0.08)",
                border: "1px solid rgba(212,175,55,0.25)",
                color: "#f4e5c3",
              }}
            >
              <option value="" disabled>
                選択してください
              </option>
              {BIRTH_MONTH_OPTIONS.map((o) => (
                <option key={o.v} value={o.v}>
                  {o.label}
                </option>
              ))}
            </select>
            <p className="text-[10px] mt-1.5 leading-relaxed" style={{ color: "rgba(244,229,195,0.4)" }}>
              誕生月にはクーポンをお届けします（年・日は不要です）。通知のためアプリのプッシュを許可してください。
            </p>
          </div>

          <div>
            <label className="block text-xs mb-1" style={{ color: "rgba(244,229,195,0.6)" }}>
              メールアドレス <span style={{ color: "#ff8080" }}>*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                clearError();
                setLocalError(null);
                setEmail(e.target.value);
              }}
              required
              autoComplete="email"
              className="w-full rounded-xl px-4 py-3 text-base outline-none"
              style={{
                background: "rgba(212,175,55,0.08)",
                border: "1px solid rgba(212,175,55,0.25)",
                color: "#f4e5c3",
              }}
              placeholder="example@email.com"
            />
          </div>

          <div>
            <label className="block text-xs mb-1" style={{ color: "rgba(244,229,195,0.6)" }}>
              パスワード（8文字以上） <span style={{ color: "#ff8080" }}>*</span>
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => {
                clearError();
                setLocalError(null);
                setPassword(e.target.value);
              }}
              required
              minLength={8}
              autoComplete="new-password"
              className="w-full rounded-xl px-4 py-3 text-base outline-none"
              style={{
                background: "rgba(212,175,55,0.08)",
                border: "1px solid rgba(212,175,55,0.25)",
                color: "#f4e5c3",
              }}
              placeholder="8文字以上"
            />
          </div>

          <div>
            <label className="block text-xs mb-1" style={{ color: "rgba(244,229,195,0.6)" }}>
              パスワード（確認） <span style={{ color: "#ff8080" }}>*</span>
            </label>
            <input
              type="password"
              value={password2}
              onChange={(e) => {
                clearError();
                setLocalError(null);
                setPassword2(e.target.value);
              }}
              required
              minLength={8}
              autoComplete="new-password"
              className="w-full rounded-xl px-4 py-3 text-base outline-none"
              style={{
                background: "rgba(212,175,55,0.08)",
                border: "1px solid rgba(212,175,55,0.25)",
                color: "#f4e5c3",
              }}
              placeholder="もう一度入力"
            />
          </div>

          <label
            className="flex items-start gap-2.5 rounded-xl px-3 py-2.5 cursor-pointer"
            style={{
              background: "rgba(212,175,55,0.06)",
              border: "1px solid rgba(212,175,55,0.22)",
            }}
          >
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => {
                clearError();
                setLocalError(null);
                setAgreedToTerms(e.target.checked);
              }}
              required
              className="mt-0.5 accent-[rgb(212,175,55)]"
            />
            <span className="text-xs leading-relaxed" style={{ color: "rgba(244,229,195,0.85)" }}>
              <Link to="/terms" className="underline" style={{ color: "#d4af37" }}>
                利用規約
              </Link>
              に同意します。
            </span>
          </label>

          <label
            className="flex items-start gap-2.5 rounded-xl px-3 py-2.5 cursor-pointer"
            style={{
              background: "rgba(212,175,55,0.06)",
              border: "1px solid rgba(212,175,55,0.22)",
            }}
          >
            <input
              type="checkbox"
              checked={agreedToPrivacy}
              onChange={(e) => {
                clearError();
                setLocalError(null);
                setAgreedToPrivacy(e.target.checked);
              }}
              required
              className="mt-0.5 accent-[rgb(212,175,55)]"
            />
            <span className="text-xs leading-relaxed" style={{ color: "rgba(244,229,195,0.85)" }}>
              <Link to="/privacy" className="underline" style={{ color: "#d4af37" }}>
                プライバシーポリシー
              </Link>
              に同意します。
            </span>
          </label>

          <label
            className="flex items-start gap-2.5 rounded-xl px-3 py-2.5 cursor-pointer"
            style={{
              background: "rgba(212,175,55,0.06)",
              border: "1px solid rgba(212,175,55,0.22)",
            }}
          >
            <input
              type="checkbox"
              checked={agreedToPersonalData}
              onChange={(e) => {
                clearError();
                setLocalError(null);
                setAgreedToPersonalData(e.target.checked);
              }}
              required
              className="mt-0.5 accent-[rgb(212,175,55)]"
            />
            <span className="text-xs leading-relaxed" style={{ color: "rgba(244,229,195,0.85)" }}>
              顧客情報（お名前、メールアドレス、パスワード、および今後実装予定の誕生日）の取得・利用に同意します。
            </span>
          </label>

          <label
            className="flex items-start gap-2.5 rounded-xl px-3 py-2.5 cursor-pointer"
            style={{
              background: "rgba(212,175,55,0.06)",
              border: "1px solid rgba(212,175,55,0.22)",
            }}
          >
            <input
              type="checkbox"
              checked={ageConfirmed}
              onChange={(e) => {
                clearError();
                setLocalError(null);
                setAgeConfirmed(e.target.checked);
              }}
              required
              className="mt-0.5 accent-[rgb(212,175,55)]"
            />
            <span className="text-xs leading-relaxed" style={{ color: "rgba(244,229,195,0.85)" }}>
              16歳以上であること、または保護者の同意を得ていることを確認しました。
            </span>
          </label>

          <label
            className="flex items-start gap-2.5 rounded-xl px-3 py-2.5 cursor-pointer"
            style={{
              background: "rgba(212,175,55,0.06)",
              border: "1px solid rgba(212,175,55,0.22)",
            }}
          >
            <input
              type="checkbox"
              checked={agreedToCameraUsage}
              onChange={(e) => {
                clearError();
                setLocalError(null);
                setAgreedToCameraUsage(e.target.checked);
              }}
              required
              className="mt-0.5 accent-[rgb(212,175,55)]"
            />
            <span className="text-xs leading-relaxed" style={{ color: "rgba(244,229,195,0.85)" }}>
              QR読み取り機能でカメラ権限を利用することに同意します。
            </span>
          </label>

          {displayError && (
            <p className="text-xs text-center" style={{ color: "#ff8080" }}>
              {displayError}
            </p>
          )}

          <button
            type="submit"
            disabled={loading || !firebaseConfigured}
            className="w-full py-3.5 rounded-2xl text-sm font-bold tracking-wider disabled:opacity-50"
            style={{
              background: "linear-gradient(135deg, #b8860b 0%, #d4af37 50%, #b8860b 100%)",
              color: "#1a1a2e",
              fontFamily: "Montserrat, sans-serif",
            }}
          >
            {loading ? "登録中…" : "登録してはじめる"}
          </button>
        </form>

        <p className="text-center text-[10px] leading-relaxed" style={{ color: "rgba(244,229,195,0.35)" }}>
          登録後、ホーム画面に表示される会員番号をメモすると、次回のログインがスムーズです。
        </p>

        <div className="text-center">
          <Link to="/login" className="text-xs underline" style={{ color: "rgba(212,175,55,0.7)" }}>
            すでに会員の方はログイン
          </Link>
        </div>

        <ShopContactSection className="pt-4" />
      </div>
    </div>
  );
}
